import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, Mail } from 'lucide-react';
import { generateWebPageSchema, generateBreadcrumbSchema } from '../utils/schemaMarkup';
import { useSchemaMarkup } from '../utils/useSchemaMarkup';

const TermsConditionsPage = () => {
  // Generate schemas
  const webPageSchema = generateWebPageSchema({
    name: 'Terms & Conditions - ComfortMaster Heating & Air',
    description: 'Terms and Conditions for Comfort Master Heating & Air services, including SMS consent, message types, and usage policies.',
    url: 'https://comfortmasterheating.com/terms-conditions',
    datePublished: '2024-04-27',
    dateModified: '2024-04-27'
  });
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://comfortmasterheating.com' },
    { name: 'Terms & Conditions', url: 'https://comfortmasterheating.com/terms-conditions' }
  ]);

  // Inject schemas into document head
  useSchemaMarkup([webPageSchema, breadcrumbSchema], 'terms-conditions');

  return (
    <main>
      <Helmet>
        <title>Terms & Conditions | ComfortMaster Heating & Air</title>
        <meta name="description" content="Terms and Conditions for Comfort Master Heating & Air services, including SMS consent, message types, and usage policies." />
        <link rel="canonical" href="https://comfortmasterheating.com/terms-conditions" />
      </Helmet>

      {/* Hero */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-emerald-400 font-medium">LEGAL</span>
          <h1 className="font-heading font-bold text-4xl md:text-5xl mt-2">Terms & Conditions</h1>
          <p className="text-stone-400 mt-4">Effective Date: April 27, 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">1. Consent</h2>
            <p className="text-slate-700 mb-6">
              You agree to receive communications via phone, email, and SMS.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">2. Message Types</h2>
            <p className="text-slate-700 mb-6">
              Reminders, updates, support, and marketing.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">3. Frequency</h2>
            <p className="text-slate-700 mb-6">
              Varies based on interaction.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">4. Rates</h2>
            <p className="text-slate-700 mb-6">
              Message and data rates may apply.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">5. Opt-Out</h2>
            <p className="text-slate-700 mb-6">
              Reply STOP to unsubscribe or HELP for support.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">6. Contact</h2>
            <div className="bg-slate-50 p-6 rounded-xl mb-6">
              <p className="font-bold text-slate-900 mb-2">Comfort Master Heating & Air</p>
              <p className="text-slate-700 flex items-center gap-2 mb-1">
                <Phone size={16} className="text-emerald-600" />
                <a href="tel:8593576803" className="text-emerald-600 hover:underline">(859) 357-6803</a>
              </p>
              <p className="text-slate-700 flex items-center gap-2">
                <Mail size={16} className="text-emerald-600" />
                <a href="mailto:info@comfortmasterheating.com" className="text-emerald-600 hover:underline">info@comfortmasterheating.com</a>
              </p>
            </div>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">7. Use</h2>
            <p className="text-slate-700 mb-6">
              Provide accurate info and lawful use.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">8. Liability</h2>
            <p className="text-slate-700 mb-6">
              Not liable for indirect damages.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">9. Updates</h2>
            <p className="text-slate-700 mb-6">
              Terms may change.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">10. Acceptance</h2>
            <p className="text-slate-700 mb-6">
              Use of services means acceptance.
            </p>

            <div className="mt-12 pt-8 border-t border-slate-200">
              <p className="text-slate-600 text-sm">
                See also our <Link to="/privacy-policy" className="text-emerald-600 hover:underline">Privacy Policy</Link> for information on how we handle your data.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TermsConditionsPage;
